/**
 * ScorecardDrilldown — collapsible per-scope table for the latest quarter.
 *
 * §5.BT §3.3 P3 Commit C (2026-06-19). Locked in
 *   docs/audits/2026-06-18_5bt_p3_3_dossier_p2_design.md §D.2 + §C
 *
 * Renders one row per scope (customer_vendor_id) for the most recent quarter,
 * pulled from `quarters[0].per_scope`. The §7.1 single-row reconciliation
 * status (partial_coverage / unavailable / agree / diverge) is preserved
 * per scope — no aggregation precedence at the scope level (P2 §C).
 *
 * Collapsed-by-default per wireframe §5.5; toggle opens the table.
 */
import React, { useState } from 'react';
import { getGradeColor } from './gradeColors';
import MetricInfo from '../MetricInfo';

// Lane 215 (Linear rebuild, Stage 2 — dossier child chrome): read the `.sd`
// Linear token layer so the drill-down flips with the shell in the future dark
// lane. The getGradeColor cells and MetricInfo header glyphs are untouched.
const LINEAR_PANEL = {
  background: 'var(--panel)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  boxShadow: 'var(--shadow)',
};

// Column headers that carry a "what does this number mean?" explainer (Lane 38).
// One glyph per column concept (not per cell) — keyed to the registry.
const HEADER_METRIC_KEYS = {
  'Scorecard YoY': 'supplier_yoy_reported',
  'WM YoY · qtr': 'supplier_yoy_wingman',
  'Status': 'supplier_yoy_reconciliation',
};

function formatPct(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return 'Not available';
  return `${Number(v).toFixed(1)}%`;
}

function formatPctSigned(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return 'Not available';
  const n = Number(v);
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
}

function formatCurrency(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return 'Not available';
  return `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

// Lane 215: status -> Linear badge tone class + human label. The .sd-badge
// chrome comes from SupplierDetail.linear.css (renders inside the dossier .sd
// scope). Labels preserved verbatim.
const STATUS_TONE = {
  agree:            { cls: 'good', label: 'agree' },
  diverge:          { cls: 'warn', label: 'diverge' },
  partial_coverage: { cls: 'mut',  label: 'partial coverage' },
  unavailable:      { cls: 'mut',  label: 'unavailable' },
};

function StatusPill({ status }) {
  if (!status) return <span style={{ color: 'var(--text-3)' }}>Not available</span>;
  const tone = STATUS_TONE[status] || { cls: 'mut', label: status };
  return (
    <span
      className={`sd-badge ${tone.cls}`}
      title={`Wingman YoY reconciliation: ${tone.label}`}
    >
      {tone.label}
    </span>
  );
}

function GradeCell({ grade }) {
  const style = getGradeColor(grade);
  return (
    <span
      data-grade={grade || 'NONE'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '28px',
        height: '28px',
        borderRadius: '6px',
        backgroundColor: style.bg,
        color: style.fg,
        border: `1px solid ${style.fg}`,
        fontWeight: 'var(--font-weight-bold)',
        fontSize: 'var(--font-size-sm)',
      }}
    >
      {grade || 'Not graded'}
    </span>
  );
}

/**
 * ScorecardDrilldown. Props:
 *   - quarters : array of quarter blocks from the endpoint response (DESC).
 *   - scopes   : array of {customer_vendor_id, scope_label, is_active} from the
 *                endpoint response — used to enrich per-scope rows with their
 *                human-readable scope label.
 *   - collapsedByDefault : bool (default true)
 */
function ScorecardDrilldown({ quarters, scopes, collapsedByDefault = true }) {
  const [open, setOpen] = useState(!collapsedByDefault);
  const list = quarters || [];
  if (list.length === 0) return null;
  const latest = list[0];
  if (!latest || !latest.per_scope || latest.per_scope.length === 0) return null;

  const scopeLabelMap = {};
  (scopes || []).forEach((s) => {
    scopeLabelMap[s.customer_vendor_id] = s.scope_label;
  });

  return (
    <div style={{ ...LINEAR_PANEL, padding: 0, marginBottom: '24px', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="drilldown-toggle"
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 20px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'inherit',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              color: 'var(--text-3)',
              fontSize: 'var(--font-size-xs)',
              width: '12px',
              flexShrink: 0,
              transition: 'transform 0.2s ease',
            }}
          >
            {open ? '▾' : '▸'}
          </span>
          <span
            style={{
              fontSize: 'var(--font-size-md)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--text)',
            }}
          >
            Per-scope drill-down
          </span>
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-3)' }}>
            {latest.period_label || ''} · {latest.per_scope.length} scope{latest.per_scope.length === 1 ? '' : 's'}
          </span>
        </div>
      </button>

      {open && (
        <div style={{ overflowX: 'auto', borderTop: '1px solid var(--hair)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Scope', 'Grade', 'OTD%', 'Spend', 'CoPP', 'Scorecard YoY', 'WM YoY · qtr', 'Status'].map((h, idx) => (
                  <th
                    key={h}
                    style={{
                      textAlign: idx >= 2 && idx <= 6 ? 'right' : 'left',
                      padding: '10px 12px',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--text-3)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                      borderBottom: '1px solid var(--border)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {HEADER_METRIC_KEYS[h] ? (
                      <MetricInfo metricKey={HEADER_METRIC_KEYS[h]}>{h}</MetricInfo>
                    ) : (
                      h
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {latest.per_scope.map((row) => {
                const cvm = row.customer_vendor_id;
                const label = scopeLabelMap[cvm] || cvm;
                const copp = (row.late_dollars || 0) + (row.vc_dollars || 0);
                return (
                  <tr
                    key={cvm}
                    data-testid="drilldown-row"
                    data-cvm={cvm}
                    style={{ borderBottom: '1px solid var(--hair)' }}
                  >
                    <td style={{ padding: '10px 12px', fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
                      <div style={{ fontWeight: 'var(--font-weight-medium)' }}>{label}</div>
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-3)' }}>CVM {cvm}</div>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <GradeCell grade={row.overall_rating} />
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 'var(--font-size-sm)', color: 'var(--text)', textAlign: 'right', fontFamily: 'monospace' }}>
                      {formatPct(row.premium_ontime_pct)}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 'var(--font-size-sm)', color: 'var(--text)', textAlign: 'right', fontFamily: 'monospace' }}>
                      {formatCurrency(row.total_value_received)}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 'var(--font-size-sm)', color: 'var(--text)', textAlign: 'right', fontFamily: 'monospace' }}>
                      {formatCurrency(copp || null)}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 'var(--font-size-sm)', color: 'var(--text)', textAlign: 'right', fontFamily: 'monospace' }}>
                      {formatPctSigned(row.src_price_change_pct)}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 'var(--font-size-sm)', color: 'var(--text)', textAlign: 'right', fontFamily: 'monospace' }}>
                      {formatPctSigned(row.wm_yoy_price_change_pct)}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <StatusPill status={row.wm_yoy_reconciliation_status} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ScorecardDrilldown;
